import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import clsx from 'clsx';
import styles from './Input.module.css';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  leftIcon?: ReactNode;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, required, hint, error, leftIcon, className, id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <div className={styles.field}>
      {label ? (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required ? <span className={styles.required}>*</span> : null}
        </label>
      ) : null}
      <div className={styles.control}>
        {leftIcon ? (
          <span className={styles.iconLeft} aria-hidden="true">
            {leftIcon}
          </span>
        ) : null}
        <input
          id={inputId}
          ref={ref}
          className={clsx(
            styles.input,
            leftIcon && styles.inputWithIcon,
            error && styles.invalid,
            className,
          )}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined
          }
          {...rest}
        />
      </div>
      {error ? (
        <span id={`${inputId}-err`} className={styles.error}>
          {error}
        </span>
      ) : hint ? (
        <span id={`${inputId}-hint`} className={styles.hint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  required?: boolean;
  hint?: string;
  error?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, required, hint, error, className, id, ...rest },
  ref,
) {
  const tid = id ?? rest.name;
  return (
    <div className={styles.field}>
      {label ? (
        <label htmlFor={tid} className={styles.label}>
          {label}
          {required ? <span className={styles.required}>*</span> : null}
        </label>
      ) : null}
      <textarea
        id={tid}
        ref={ref}
        className={clsx(styles.textarea, error && styles.invalid, className)}
        aria-invalid={!!error}
        {...rest}
      />
      {error ? (
        <span className={styles.error}>{error}</span>
      ) : hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
});
